/*
 * HP side cart — slide-out drawer that replaces the add-to-cart popup.
 *
 * The section it drives is re-rendered region by region (every element with
 * data-hp-sc-region has its innerHTML swapped), so every listener lives on the
 * custom element itself and works by delegation.
 *
 * Loaded with a classic deferred <script>: no modules, no globals except the
 * custom element registration.
 */
(function () {
  if (customElements.get('hp-side-cart')) return;

  var SECTION_ID = 'hp-side-cart';
  var BUBBLE_ID = 'cart-icon-bubble';
  var POTS_ID = 'hp-side-cart-pots';
  var SECTIONS_PARAM = SECTION_ID + ',' + BUBBLE_ID;

  var QTY_DEBOUNCE = 250;
  var NOTE_DEBOUNCE = 500;
  var NOTE_SAVED_MS = 2500;
  var ERROR_MS = 5000;
  var NEW_ITEM_MS = 1600;
  var JUST_ADDED_MS = 4000;
  var ADDED_MS = 500;
  var FOCUS_DELAY = 60;
  var SCROLL_GAP = 12;
  var COMPACT_TRAVEL = 12; // px scrolled one way before the summary folds or unfolds
  var COMPACT_LOCK_MS = 250; // outlasts the 200ms fold, which resizes the body under the scroll

  /* The drawer keeps its own focus trap: the theme's global one snapshots the
   * first/last focusable once, ignores visibility, and goes stale the moment a
   * region is swapped. */
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
    ' textarea:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /* Controls worth putting focus back on after a region swap destroys them */
  var FOCUS_CONTROLS = [
    '[data-hp-sc-qty="minus"]',
    '[data-hp-sc-qty="plus"]',
    '[data-hp-sc-qty-input]',
    '[data-hp-sc-remove]',
    '[data-hp-sc-pot-toggle]',
    '[data-hp-sc-add]',
    '[data-hp-sc-weekend-btn]'
  ];

  /* Keys that mean someone is navigating by keyboard and wants a focus ring */
  var NAV_KEYS = [
    'Tab',
    'Enter',
    ' ',
    'Spacebar',
    'Escape',
    'Esc',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight'
  ];

  var globalsBound = false;
  /* Pointer is the safe default: this file can load after the first interaction */
  var lastInputKeyboard = false;

  /* ------------------------------------------------------------------ *
   * Small local helpers (the theme's debounce/fetchConfig are not
   * guaranteed to have run before this file, so nothing is depended on).
   * ------------------------------------------------------------------ */

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cartRoute(name, path) {
    var routes = window.routes || {};
    return routes[name] || shopRoot() + path;
  }

  function cartJsUrl() {
    return shopRoot() + 'cart.js';
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json();
    });
  }

  function getJson(url) {
    return fetch(url).then(function (response) {
      return response.json();
    });
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  /* UK "now", used for the weekend-delivery ordering window */
  function ukNow() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function isVisible(node) {
    if (!node || node.closest('[hidden]')) return false;
    return node.offsetParent !== null || node.getClientRects().length > 0;
  }

  function focusSafely(node) {
    if (!node || typeof node.focus !== 'function') return false;
    node.focus({ preventScroll: true });
    return true;
  }

  /* Locate the weekend delivery line in a /cart.js payload, by variant id or handle */
  function findWeekendLine(cart, variantId, handle) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if ((variantId && item.id === variantId) || (handle && item.handle === handle)) return item;
    }
    return null;
  }

  function errorMessage(state) {
    return (state && (state.description || state.message)) || '';
  }

  /* ------------------------------------------------------------------ *
   * Document-level listeners. Registered once, and they look the drawer
   * up at event time so they survive shopify:section:load.
   * ------------------------------------------------------------------ */

  function currentDrawer() {
    var drawer = document.querySelector('hp-side-cart');
    return drawer && typeof drawer.open === 'function' ? drawer : null;
  }

  function onDocumentClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!event.target || !event.target.closest) return;

    var trigger = event.target.closest('#' + BUBBLE_ID + ', [data-hp-side-cart-open]');
    if (!trigger) return;

    var drawer = currentDrawer();
    if (!drawer) return;

    event.preventDefault();
    drawer.open(trigger);
    // Opened from the header: the markup may be stale, so re-pull it quietly.
    drawer.refresh();
  }

  function bindGlobalListeners() {
    if (globalsBound) return;
    globalsBound = true;

    document.addEventListener('click', onDocumentClick);

    // Track the input modality so opening the drawer only shows a focus ring
    // to keyboard users: focusing after a fetch reads as :focus-visible.
    var capture = { capture: true, passive: true };
    document.addEventListener(
      'keydown',
      function (event) {
        if (NAV_KEYS.indexOf(event.key) !== -1) lastInputKeyboard = true;
      },
      capture
    );
    ['pointerdown', 'mousedown', 'touchstart'].forEach(function (name) {
      document.addEventListener(
        name,
        function () {
          lastInputKeyboard = false;
        },
        capture
      );
    });

    if (!window.Shopify || !window.Shopify.designMode) return;

    document.addEventListener('shopify:section:select', function (event) {
      var drawer = currentDrawer();
      if (!drawer || !event.target || !event.target.contains(drawer)) return;
      drawer.open();
    });

    document.addEventListener('shopify:section:deselect', function (event) {
      var drawer = currentDrawer();
      if (!drawer || !event.target || !event.target.contains(drawer)) return;
      drawer.close();
    });
  }

  /* ------------------------------------------------------------------ */

  class HpSideCart extends HTMLElement {
    constructor() {
      super();

      this.initialised = false;
      this.queue = Promise.resolve();
      this.mutationSeq = 0;
      this.renderGeneration = 0;
      this.busyCount = 0;
      this.qtyTimers = {};
      this.potData = null;
      this.potRequest = null;
      this.weekendExpiryRunning = false;
      this.lastScrollTop = 0;
      this.scrollTravel = 0;
      this.compactLockUntil = 0;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onBodyScroll = this.onBodyScroll.bind(this);
    }

    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.panel = this.querySelector('.hp-sc__panel');
      this.scrollBody = this.querySelector('[data-hp-sc-body]');
      this.closeButton = this.querySelector('.hp-sc__close');

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('pageshow', this.onPageShow);
      if (this.scrollBody) this.scrollBody.addEventListener('scroll', this.onBodyScroll, { passive: true });

      var bubble = document.getElementById(BUBBLE_ID);
      if (bubble) bubble.setAttribute('aria-haspopup', 'dialog');

      bindGlobalListeners();
      this.applyWeekendState();
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.onPageShow);
      // Never leave the page scroll-locked behind a drawer that has gone away.
      if (this.classList.contains('is-open')) document.body.classList.remove('overflow-hidden');
    }

    /* ---------------------------------------------------------------- *
     * Public API (also used by cart-notification.js)
     * ---------------------------------------------------------------- */

    getSectionsToRender() {
      return [{ id: SECTION_ID }, { id: BUBBLE_ID }];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    open(opener) {
      var wasOpen = this.classList.contains('is-open');
      if (!wasOpen) this.opener = opener || this.activeElement || document.activeElement;

      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');

      if (!wasOpen) {
        this.setCompact(false);
        this.lastScrollTop = this.scrollBody ? this.scrollBody.scrollTop : 0;

        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(
          function () {
            // Pointer users get the silent dialog (role + aria-labelledby still
            // announce it); keyboard users land on the close button.
            var target = lastInputKeyboard
              ? this.closeButton || this.panel
              : this.panel || this.closeButton;
            focusSafely(target);
          }.bind(this),
          FOCUS_DELAY
        );
      }

      document.dispatchEvent(new CustomEvent('hp:side-cart:opened'));
      this.checkWeekendExpiry();
    }

    close() {
      this.classList.remove('is-open', 'hp-sc--just-added');
      document.body.classList.remove('overflow-hidden');
      clearTimeout(this.focusTimer);

      var opener = this.opener;
      this.opener = null;
      this.activeElement = null;

      // Our trap is just the Tab handler below, so there is nothing to undo —
      // hand focus back to whatever opened the drawer, if it is still around.
      if (opener && !opener.disabled && document.contains(opener)) focusSafely(opener);

      document.dispatchEvent(new CustomEvent('hp:side-cart:closed'));
    }

    /* Pull the section markup again without mutating the cart */
    refresh() {
      var seq = this.mutationSeq;
      return getJson(window.location.pathname + '?sections=' + SECTIONS_PARAM)
        .then(
          function (sections) {
            // A mutation started after this request will render newer markup.
            if (seq !== this.mutationSeq) return;
            this.renderSections(sections);
          }.bind(this)
        )
        .catch(function () {
          /* a failed refresh just leaves the current markup in place */
        });
    }

    /*
     * Called by cart-notification.js after every add to cart.
     * true  = the drawer handled it (it is open, popup must not show)
     * false = silently updated only, the legacy popup continues
     */
    renderContents(state) {
      // Read now: auto-open clears the global before a deferred refresh lands.
      var ids = window.recentlyAddedItems;

      if (state && state.sections && state.sections[SECTION_ID]) {
        this.renderSections(state.sections);
        this.highlightRecentlyAdded(ids);
      } else {
        // The caller did not ask for our section — fetch it ourselves.
        this.refresh().then(
          function () {
            this.highlightRecentlyAdded(ids);
          }.bind(this)
        );
      }

      document.dispatchEvent(new CustomEvent('hp:side-cart:rendered'));

      if (this.getAttribute('data-auto-open') === 'false') return false;

      this.classList.add('hp-sc--just-added');
      clearTimeout(this.justAddedTimer);
      this.justAddedTimer = setTimeout(
        function () {
          this.classList.remove('hp-sc--just-added');
        }.bind(this),
        JUST_ADDED_MS
      );

      this.open();
      window.recentlyAddedItems = null;
      return true;
    }

    /* ---------------------------------------------------------------- *
     * Rendering
     * ---------------------------------------------------------------- */

    renderSections(sections) {
      if (!sections) return;

      var html = sections[SECTION_ID];
      if (html) {
        var source = new DOMParser().parseFromString(html, 'text/html').querySelector('hp-side-cart');
        if (source) {
          var scrollTop = this.scrollBody ? this.scrollBody.scrollTop : 0;
          var focused = this.captureFocus();
          var fill = this.querySelector('.hp-sc__bar-fill');
          var oldWidth = fill ? fill.style.width : '';

          this.querySelectorAll('[data-hp-sc-region]').forEach(function (region) {
            var name = region.getAttribute('data-hp-sc-region');
            var next = source.querySelector('[data-hp-sc-region="' + name + '"]');
            if (next) region.innerHTML = next.innerHTML;
          });

          if (source.hasAttribute('data-item-count')) {
            this.setAttribute('data-item-count', source.getAttribute('data-item-count'));
          }
          if (source.hasAttribute('data-weekend-handle')) {
            this.setAttribute('data-weekend-handle', source.getAttribute('data-weekend-handle'));
          }
          this.classList.toggle('is-empty', source.classList.contains('is-empty'));

          if (this.scrollBody) {
            this.scrollBody.scrollTop = scrollTop;
            // Folded with nothing left to scroll, the breakdown could never come back.
            if (this.scrollBody.scrollHeight <= this.scrollBody.clientHeight) this.setCompact(false);
          }
          this.restoreFocus(focused);
          this.animateDeliveryBar(oldWidth);
        }
      }

      var bubbleHTML = sections[BUBBLE_ID];
      if (bubbleHTML) {
        var bubble = document.getElementById(BUBBLE_ID);
        var parsedBubble = new DOMParser()
          .parseFromString(bubbleHTML, 'text/html')
          .querySelector('.shopify-section');
        if (bubble && parsedBubble) bubble.innerHTML = parsedBubble.innerHTML;
      }

      // The trays live inside the items region, so both they and their data go.
      this.renderGeneration += 1;
      this.potData = null;
      this.potRequest = null;

      this.applyWeekendState();

      document.dispatchEvent(
        new CustomEvent('hp:side-cart:updated', {
          detail: { itemCount: parseInt(this.getAttribute('data-item-count'), 10) || 0 }
        })
      );
    }

    /*
     * The swap replaces the fill node, so the CSS width transition never runs.
     * Put the old width back on the new node, flush it, then set the target.
     */
    animateDeliveryBar(oldWidth) {
      if (!oldWidth || !this.classList.contains('is-open')) return;

      var fill = this.querySelector('.hp-sc__bar-fill');
      if (!fill) return;

      var target = fill.style.width;
      if (!target || target === oldWidth) return;

      fill.style.width = oldWidth;
      fill.getBoundingClientRect(); // force a reflow
      fill.style.width = target;
    }

    highlightRecentlyAdded(ids) {
      if (!ids || !ids.length) return;

      var wanted = ids.map(String);
      var first = null;

      this.querySelectorAll('[data-hp-sc-item][data-variant-id]').forEach(function (item) {
        if (wanted.indexOf(item.getAttribute('data-variant-id')) === -1) return;
        item.classList.add('is-new');
        setTimeout(function () {
          item.classList.remove('is-new');
        }, NEW_ITEM_MS);
        if (!first) first = item;
      });

      if (first) this.scrollBodyTo(first);
    }

    /* ---------------------------------------------------------------- *
     * Events
     * ---------------------------------------------------------------- */

    onClick(event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest('[data-hp-sc-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (target.closest('[data-hp-sc-notice] button')) {
        this.hideNotice();
        return;
      }

      var qtyButton = target.closest('[data-hp-sc-qty]');
      if (qtyButton) {
        this.onQuantityButton(qtyButton);
        return;
      }

      var removeButton = target.closest('[data-hp-sc-remove]');
      if (removeButton) {
        this.onRemove(removeButton);
        return;
      }

      var addButton = target.closest('[data-hp-sc-add]');
      if (addButton) {
        this.onAdd(addButton);
        return;
      }

      var potToggle = target.closest('[data-hp-sc-pot-toggle]');
      if (potToggle) {
        this.onPotToggle(potToggle);
        return;
      }

      var weekendButton = target.closest('[data-hp-sc-weekend-btn]');
      if (weekendButton) this.onWeekendButton(weekendButton);
    }

    onChange(event) {
      var target = event.target;
      if (!target || !target.matches) return;

      if (target.matches('[data-hp-sc-qty-input]')) {
        var item = target.closest('[data-hp-sc-item]');
        if (!item) return;
        var quantity = parseInt(target.value, 10);
        if (isNaN(quantity) || quantity < 0) quantity = parseInt(target.defaultValue, 10) || 0;
        target.value = quantity;
        this.scheduleQuantity(item, quantity);
        return;
      }

      if (target.matches('[data-hp-sc-note-input]')) {
        clearTimeout(this.noteTimer);
        this.saveNote(target.value);
      }
    }

    onInput(event) {
      var target = event.target;
      if (!target || !target.matches || !target.matches('[data-hp-sc-note-input]')) return;

      var value = target.value;
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(
        function () {
          this.saveNote(value);
        }.bind(this),
        NOTE_DEBOUNCE
      );
    }

    onKeydown(event) {
      if (event.key === 'Tab') {
        this.onTab(event);
        return;
      }

      if (event.key !== 'Escape' && event.key !== 'Esc') return;

      var openTray = this.querySelector('[data-hp-sc-pot-tray]:not([hidden])');
      if (openTray) {
        var item = openTray.closest('[data-hp-sc-item]');
        var chip = item ? item.querySelector('[data-hp-sc-pot-toggle][aria-expanded]') : null;
        this.closeTrays();
        if (chip) chip.focus();
        return;
      }

      this.close();
    }

    onPageShow(event) {
      // Back/forward cache: the cart may have changed on the page we came from.
      if (event.persisted) this.refresh();
    }

    /* ---------------------------------------------------------------- *
     * Focus
     * ---------------------------------------------------------------- */

    /* Computed live: the empty state and the trays hide whole branches */
    focusables() {
      if (!this.panel) return [];
      return Array.prototype.filter.call(this.panel.querySelectorAll(FOCUSABLE), isVisible);
    }

    onTab(event) {
      var items = this.focusables();
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      if (!active || !this.panel.contains(active)) {
        // Focus slipped out of the dialog — pull it back in.
        event.preventDefault();
        focusSafely(first);
        return;
      }

      // The panel itself is the opening focus target for pointer users, and
      // Shift+Tab from it would otherwise walk backwards out of the dialog.
      if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        focusSafely(last);
        return;
      }

      if (!event.shiftKey && active === last) {
        event.preventDefault();
        focusSafely(first);
      }
    }

    /* Describe the focused control so it can be found again after a swap */
    captureFocus() {
      var active = document.activeElement;
      if (!active || !active.closest || !this.contains(active)) return null;

      var item = active.closest('[data-hp-sc-item]');
      var descriptor = {
        element: active,
        key: item ? item.getAttribute('data-key') : null,
        control: null,
        variantId: null
      };

      for (var i = 0; i < FOCUS_CONTROLS.length; i++) {
        if (active.closest(FOCUS_CONTROLS[i])) {
          descriptor.control = FOCUS_CONTROLS[i];
          break;
        }
      }

      // The tray (and its close button) never survives a swap — aim at the chip.
      if (descriptor.control === '[data-hp-sc-pot-toggle]') {
        descriptor.control = '[data-hp-sc-pot-toggle][aria-expanded]';
      }

      if (descriptor.control === '[data-hp-sc-add]') {
        descriptor.variantId = active.closest('[data-hp-sc-add]').getAttribute('data-variant-id');
      }

      return descriptor;
    }

    restoreFocus(descriptor) {
      if (!descriptor || !this.classList.contains('is-open')) return;

      // Only step in when the swap actually dropped focus.
      var active = document.activeElement;
      if (active && active !== document.body && this.contains(active)) return;
      if (descriptor.element && document.contains(descriptor.element)) return;

      var target = null;
      if (descriptor.control) {
        var scope = descriptor.key ? this.findItem(descriptor.key) : this;
        if (scope) {
          var candidates = Array.prototype.slice.call(scope.querySelectorAll(descriptor.control));
          if (descriptor.variantId) {
            candidates = candidates.filter(function (node) {
              return node.getAttribute('data-variant-id') === descriptor.variantId;
            });
          }
          target = candidates.filter(isVisible)[0] || null;
        }
      }

      // The line (or the whole control) is gone: the dialog itself takes focus.
      focusSafely(target || this.panel);
    }

    /*
     * Scroll the drawer body only. scrollIntoView would also move ancestor
     * scrollers, and it runs while the panel is still translated off-screen.
     */
    scrollBodyTo(target) {
      var scroller = this.scrollBody;
      if (!scroller || !target) return;

      var viewport = scroller.getBoundingClientRect();
      var box = target.getBoundingClientRect();
      var delta = 0;

      if (box.top < viewport.top + SCROLL_GAP) {
        delta = box.top - viewport.top - SCROLL_GAP;
      } else if (box.bottom > viewport.bottom - SCROLL_GAP) {
        // Nearest edge: never push the top of a tall element out of view.
        delta = Math.min(box.bottom - viewport.bottom + SCROLL_GAP, box.top - viewport.top - SCROLL_GAP);
      }
      if (!delta) return;

      var top = Math.max(0, scroller.scrollTop + delta);
      if (typeof scroller.scrollTo === 'function' && !prefersReducedMotion()) {
        scroller.scrollTo({ top: top, behavior: 'smooth' });
      } else {
        scroller.scrollTop = top;
      }
    }

    /* ---------------------------------------------------------------- *
     * Compact summary: scrolling the body down folds the footer breakdown
     * (subtotal, delivery, savings…) away, leaving only the Total; scrolling
     * back up, or reaching the top, unfolds it.
     * ---------------------------------------------------------------- */

    onBodyScroll() {
      var scroller = this.scrollBody;
      var top = scroller.scrollTop;
      var delta = top - this.lastScrollTop;
      this.lastScrollTop = top;

      if (top <= 0) {
        this.setCompact(false);
        return;
      }

      // Folding grows the body, which can clamp scrollTop — that is not the shopper.
      if (Date.now() < this.compactLockUntil) return;

      // Only travel in one unbroken direction counts.
      this.scrollTravel = (delta > 0) === (this.scrollTravel > 0) ? this.scrollTravel + delta : delta;

      if (this.scrollTravel > COMPACT_TRAVEL) {
        if (this.canFold()) this.setCompact(true);
      } else if (this.scrollTravel < -COMPACT_TRAVEL) {
        // Pinned to the end means the fold clamped scrollTop; stay folded.
        if (top < scroller.scrollHeight - scroller.clientHeight - 1) this.setCompact(false);
      }
    }

    /* Only fold when the body would still scroll afterwards, or the fold would undo itself */
    canFold() {
      if (this.classList.contains('hp-sc--compact')) return false;
      var breakdown = this.querySelector('.hp-sc__breakdown');
      if (!breakdown) return false;
      var room = this.scrollBody.scrollHeight - this.scrollBody.clientHeight;
      return room > breakdown.offsetHeight + COMPACT_TRAVEL;
    }

    setCompact(on) {
      if (this.classList.contains('hp-sc--compact') === on) return;
      this.classList.toggle('hp-sc--compact', on);
      this.scrollTravel = 0;
      this.compactLockUntil = Date.now() + COMPACT_LOCK_MS;
    }

    /* ---------------------------------------------------------------- *
     * Quantity and remove
     * ---------------------------------------------------------------- */

    onQuantityButton(button) {
      var item = button.closest('[data-hp-sc-item]');
      if (!item) return;

      var input = item.querySelector('[data-hp-sc-qty-input]');
      if (!input) return;

      var current = parseInt(input.value, 10);
      if (isNaN(current)) current = 0;

      var next = button.getAttribute('data-hp-sc-qty') === 'plus' ? current + 1 : current - 1;
      if (next < 0) next = 0;
      if (next === current) return;

      input.value = next; // optimistic, the commit is debounced
      this.scheduleQuantity(item, next);
    }

    onRemove(button) {
      var item = button.closest('[data-hp-sc-item]');
      if (!item) return;

      var key = item.getAttribute('data-key');
      if (!key) return;

      // A remove wins over any quantity change still waiting to be sent.
      clearTimeout(this.qtyTimers[key]);
      delete this.qtyTimers[key];
      item.classList.add('is-loading');
      this.enqueue(
        function () {
          return this.commitQuantity(key, 0);
        }.bind(this)
      );
    }

    scheduleQuantity(item, quantity) {
      var key = item.getAttribute('data-key');
      if (!key) return;

      clearTimeout(this.qtyTimers[key]);
      this.qtyTimers[key] = setTimeout(
        function () {
          delete this.qtyTimers[key];
          this.enqueue(
            function () {
              return this.commitQuantity(key, quantity);
            }.bind(this)
          );
        }.bind(this),
        QTY_DEBOUNCE
      );
    }

    commitQuantity(key, quantity) {
      var item = this.findItem(key);
      if (item) item.classList.add('is-loading');
      this.setBusy(true);
      this.mutationSeq += 1;

      return postJson(cartRoute('cart_change_url', 'cart/change'), {
        id: key,
        quantity: quantity,
        sections: SECTIONS_PARAM,
        sections_url: window.location.pathname
      })
        .then(
          function (state) {
            if (state && state.status) return this.handleLineError(key, errorMessage(state));
            this.renderSections(state.sections);
          }.bind(this)
        )
        .catch(
          function () {
            this.showError(this.getAttribute('data-label-error'));
          }.bind(this)
        )
        .then(
          function () {
            var current = this.findItem(key);
            if (current) current.classList.remove('is-loading');
            this.setBusy(false);
          }.bind(this)
        );
    }

    /* Shopify rejected the change (e.g. 422): put the line back, then explain */
    handleLineError(key, message) {
      var item = this.findItem(key);
      var input = item ? item.querySelector('[data-hp-sc-qty-input]') : null;
      if (input) input.value = input.defaultValue;

      return this.refresh().then(
        function () {
          // refresh() swapped the items region, so find the line again.
          var fresh = this.findItem(key);
          var target = fresh ? fresh.querySelector('[data-hp-sc-item-error]') : null;
          if (target) {
            this.flash(target, message || this.getAttribute('data-label-error'));
          } else {
            this.showError(message || this.getAttribute('data-label-error'));
          }
        }.bind(this)
      );
    }

    findItem(key) {
      var match = null;
      this.querySelectorAll('[data-hp-sc-item]').forEach(function (item) {
        if (!match && item.getAttribute('data-key') === key) match = item;
      });
      return match;
    }

    /* ---------------------------------------------------------------- *
     * Add buttons (pot tray + plant care upsell)
     * ---------------------------------------------------------------- */

    onAdd(button) {
      if (button.disabled) return;

      var variantId = Number(button.getAttribute('data-variant-id'));
      if (!variantId) return;

      var label = button.textContent;
      var line = { id: variantId, quantity: 1 };
      if (button.dataset.pairedPlant) line.properties = { _paired_plant: button.dataset.pairedPlant };
      // Nest the pot under its plant's line (Shopify nested cart lines).
      if (button.dataset.parentKey) line.parent_line_key = button.dataset.parentKey;

      button.disabled = true;
      button.textContent = this.getAttribute('data-label-adding') || '';

      this.enqueue(
        function () {
          this.setBusy(true);
          this.mutationSeq += 1;

          return postJson(cartRoute('cart_add_url', 'cart/add'), {
            items: [line],
            sections: SECTIONS_PARAM,
            sections_url: window.location.pathname
          })
            .then(
              function (state) {
                if (state && state.status) throw new Error(errorMessage(state));
                button.textContent = this.getAttribute('data-label-added') || '';
                button.classList.add('is-added');
                // Let the "Added" state read before the region is swapped out.
                return wait(ADDED_MS).then(
                  function () {
                    this.renderSections(state.sections);
                  }.bind(this)
                );
              }.bind(this)
            )
            .catch(
              function (error) {
                button.disabled = false;
                button.classList.remove('is-added');
                button.textContent = label;
                this.showError((error && error.message) || this.getAttribute('data-label-error'));
              }.bind(this)
            )
            .then(
              function () {
                this.setBusy(false);
              }.bind(this)
            );
        }.bind(this)
      );
    }

    /* ---------------------------------------------------------------- *
     * Pot tray
     * ---------------------------------------------------------------- */

    onPotToggle(button) {
      var item = button.closest('[data-hp-sc-item]');
      if (!item) return;

      var tray = item.querySelector('[data-hp-sc-pot-tray]');
      if (!tray) return;

      var wasOpen = !tray.hidden;
      this.closeTrays(); // only one tray open at a time
      if (wasOpen) return;

      tray.hidden = false;
      var chip = item.querySelector('[data-hp-sc-pot-toggle][aria-expanded]');
      if (chip) chip.setAttribute('aria-expanded', 'true');

      this.fillPotTray(item, tray);
      this.scrollBodyTo(tray);
    }

    closeTrays() {
      this.querySelectorAll('[data-hp-sc-pot-tray]').forEach(function (tray) {
        tray.hidden = true;
      });
      this.querySelectorAll('[data-hp-sc-pot-toggle][aria-expanded]').forEach(function (chip) {
        chip.setAttribute('aria-expanded', 'false');
      });
    }

    fillPotTray(item, tray) {
      var list = tray.querySelector('[data-hp-sc-pot-list]');
      if (!list) return;

      if (this.potData) {
        if (list.childElementCount === 0) this.renderPots(item, list);
        return;
      }

      this.renderSkeletons(list);

      if (!this.potRequest) {
        this.potRequest = getJson(window.location.pathname + '?sections=' + POTS_ID)
          .then(parsePotData)
          .catch(function () {
            return [];
          });
      }

      var generation = this.renderGeneration;
      this.potRequest.then(
        function (data) {
          // A re-render since the request started threw these trays away.
          if (generation !== this.renderGeneration) return;
          this.potData = data;
          this.potRequest = null;
          if (tray.hidden) return; // closed again while loading
          this.renderPots(item, list);
        }.bind(this)
      );
    }

    renderPots(item, list) {
      var key = item.getAttribute('data-key');
      var entry = null;
      (this.potData || []).forEach(function (candidate) {
        if (!entry && candidate && candidate.plantKey === key) entry = candidate;
      });

      var pots = entry && Array.isArray(entry.pots) ? entry.pots : [];
      list.textContent = '';

      if (!pots.length) {
        list.appendChild(el('p', 'hp-sc__pot-empty', this.getAttribute('data-label-no-pots') || ''));
        return;
      }

      var fragment = document.createDocumentFragment();
      pots.forEach(
        function (pot) {
          fragment.appendChild(this.buildPotCard(pot, entry.plantHandle, entry.plantKey));
        }.bind(this)
      );
      list.appendChild(fragment);
    }

    /* Built with DOM APIs so pot titles carrying quotes or & stay safe */
    buildPotCard(pot, plantHandle, plantKey) {
      var card = el('div', 'hp-sc__mini');

      var media = el('div', 'hp-sc__mini-media');
      if (pot.image) {
        var image = el('img', 'hp-sc__mini-img');
        image.src = pot.image;
        image.alt = '';
        image.width = 88;
        image.height = 88;
        image.loading = 'lazy';
        media.appendChild(image);
      }
      card.appendChild(media);

      var title = pot.title || '';
      card.appendChild(el('div', 'hp-sc__mini-title', title));

      var subtitle = '';
      if (pot.subtitle && pot.size) subtitle = pot.subtitle + ' (' + pot.size + ')';
      else if (pot.size) subtitle = '(' + pot.size + ')';
      else if (pot.subtitle) subtitle = pot.subtitle;
      if (subtitle) card.appendChild(el('div', 'hp-sc__mini-sub', subtitle));

      var foot = el('div', 'hp-sc__mini-foot');
      foot.appendChild(el('div', 'hp-sc__mini-price', pot.priceFormatted || ''));

      var addLabel = this.getAttribute('data-label-add') || '';
      var button = el('button', 'hp-sc__mini-add', addLabel);
      button.type = 'button';
      button.setAttribute('data-hp-sc-add', '');
      button.setAttribute('data-variant-id', String(pot.variantId));
      if (plantHandle) button.setAttribute('data-paired-plant', plantHandle);
      if (plantKey) button.setAttribute('data-parent-key', plantKey);
      button.setAttribute('aria-label', 'Add ' + title + ' to cart');
      foot.appendChild(button);

      card.appendChild(foot);
      return card;
    }

    renderSkeletons(list) {
      list.textContent = '';
      for (var i = 0; i < 3; i++) {
        var card = el('div', 'hp-sc__mini hp-sc__mini--skeleton');
        card.setAttribute('aria-hidden', 'true');
        card.appendChild(document.createElement('span')); // div:empty would hide it
        list.appendChild(card);
      }
    }

    /* ---------------------------------------------------------------- *
     * Weekend delivery
     * ---------------------------------------------------------------- */

    weekendState() {
      var uk = ukNow();
      var day = uk.getDay();
      var hour = uk.getHours();
      return {
        available: (day === 3 && hour >= 12) || day === 4 || (day === 5 && hour < 12),
        friday: day === 5
      };
    }

    applyWeekendState() {
      var state = this.weekendState();
      this.classList.toggle('hp-sc--weekend-available', state.available);

      var block = this.querySelector('[data-hp-sc-weekend]');
      if (!block || block.getAttribute('data-in-cart') === 'true' || !state.friday) return;

      var label = block.querySelector('[data-hp-sc-weekend-label]');
      var friday = block.getAttribute('data-label-friday');
      if (label && friday) label.textContent = friday;
    }

    onWeekendButton(button) {
      if (button.disabled) return;

      var block = button.closest('[data-hp-sc-weekend]');
      if (!block) return;

      if (button.getAttribute('data-action') === 'remove') {
        this.weekendRemove(block, button);
      } else {
        this.weekendAdd(block, button);
      }
    }

    weekendAdd(block, button) {
      var variantId = Number(block.getAttribute('data-variant-id'));
      var handle = this.getAttribute('data-weekend-handle');
      var label = button.textContent;

      button.disabled = true;
      button.textContent = this.getAttribute('data-label-adding') || '';

      return this.enqueue(
        function () {
          this.setBusy(true);
          this.mutationSeq += 1;

          // /cart/add.js is additive, so check the live cart before adding:
          // another tab may already hold the weekend line.
          return getJson(cartJsUrl())
            .then(
              function (cart) {
                var existing = findWeekendLine(cart, variantId, handle);
                // Tag the order either way; it is idempotent and not worth failing on.
                var tagOrder = postJson(cartRoute('cart_update_url', 'cart/update'), {
                  attributes: { 'Weekend-Delivery': 'SATURDAY DELIVERY' }
                }).catch(function () {});

                if (existing) {
                  if (existing.quantity > 1) return this.changeLine(existing.key, 1);
                  return this.refresh();
                }

                return tagOrder.then(
                  function () {
                    return postJson(cartRoute('cart_add_url', 'cart/add'), {
                      items: [{ id: variantId, quantity: 1 }],
                      sections: SECTIONS_PARAM,
                      sections_url: window.location.pathname
                    }).then(
                      function (state) {
                        if (state && state.status) throw new Error(errorMessage(state));
                        // Two tabs can pass the check above at once — clamp back to 1.
                        var added = findWeekendLine({ items: state.items || [] }, variantId, handle);
                        if (added && added.key && added.quantity > 1) {
                          return this.changeLine(added.key, 1);
                        }
                        this.renderSections(state.sections);
                      }.bind(this)
                    );
                  }.bind(this)
                );
              }.bind(this)
            )
            .catch(
              function (error) {
                button.disabled = false;
                button.textContent = label;
                this.showError((error && error.message) || this.getAttribute('data-label-error'));
              }.bind(this)
            )
            .then(
              function () {
                this.setBusy(false);
              }.bind(this)
            );
        }.bind(this)
      );
    }

    weekendRemove(block, button) {
      var variantId = Number(block.getAttribute('data-variant-id'));
      var handle = this.getAttribute('data-weekend-handle');
      var label = button ? button.textContent : '';

      if (button) {
        button.disabled = true;
        button.textContent = 'Removing…';
      }

      return this.enqueue(
        function () {
          this.setBusy(true);
          this.mutationSeq += 1;

          // Resolve the line from the live cart: the rendered one can be stale.
          return getJson(cartJsUrl())
            .then(
              function (cart) {
                var existing = findWeekendLine(cart, variantId, handle);
                if (!existing) return this.refresh();

                return postJson(cartRoute('cart_update_url', 'cart/update'), {
                  attributes: { 'Weekend-Delivery': '' }
                }).then(
                  function () {
                    return this.changeLine(existing.key, 0);
                  }.bind(this)
                );
              }.bind(this)
            )
            .catch(
              function (error) {
                if (button) {
                  button.disabled = false;
                  button.textContent = label;
                }
                this.showError((error && error.message) || this.getAttribute('data-label-error'));
              }.bind(this)
            )
            .then(
              function () {
                this.setBusy(false);
              }.bind(this)
            );
        }.bind(this)
      );
    }

    changeLine(key, quantity) {
      return postJson(cartRoute('cart_change_url', 'cart/change'), {
        id: key,
        quantity: quantity,
        sections: SECTIONS_PARAM,
        sections_url: window.location.pathname
      }).then(
        function (state) {
          if (state && state.status) throw new Error(errorMessage(state));
          this.renderSections(state.sections);
        }.bind(this)
      );
    }

    /* Weekend delivery bought earlier in the week is no longer deliverable */
    checkWeekendExpiry() {
      if (this.weekendExpiryRunning) return;

      var block = this.querySelector('[data-hp-sc-weekend][data-in-cart="true"]');
      if (!block || this.weekendState().available) return;

      this.weekendExpiryRunning = true;
      this.showNotice(this.getAttribute('data-label-weekend-expired'));
      this.weekendRemove(block, null).then(
        function () {
          this.weekendExpiryRunning = false;
        }.bind(this)
      );
    }

    /* ---------------------------------------------------------------- *
     * Gift note
     * ---------------------------------------------------------------- */

    saveNote(value) {
      return this.enqueue(
        function () {
          return postJson(cartRoute('cart_update_url', 'cart/update'), { note: value })
            .then(
              function (state) {
                if (state && state.status) return;
                var saved = this.querySelector('[data-hp-sc-note-saved]');
                if (!saved) return;
                saved.hidden = false;
                clearTimeout(this.noteSavedTimer);
                this.noteSavedTimer = setTimeout(function () {
                  saved.hidden = true;
                }, NOTE_SAVED_MS);
              }.bind(this)
            )
            .catch(
              function () {
                this.showError(this.getAttribute('data-label-error'));
              }.bind(this)
            );
        }.bind(this)
      );
    }

    /* ---------------------------------------------------------------- *
     * Shared plumbing
     * ---------------------------------------------------------------- */

    /*
     * Every cart mutation runs through this chain, so a slow response can
     * never render markup that a later request has already replaced.
     */
    enqueue(task) {
      var result = this.queue.then(task);
      this.queue = result.catch(function () {});
      return result;
    }

    setBusy(busy) {
      this.busyCount = Math.max(0, this.busyCount + (busy ? 1 : -1));
      this.classList.toggle('is-busy', this.busyCount > 0);
    }

    flash(target, message) {
      if (!target) return;
      target.textContent = message || '';
      target.hidden = false;
      clearTimeout(target.hpScTimer);
      target.hpScTimer = setTimeout(function () {
        target.hidden = true;
        target.textContent = '';
      }, ERROR_MS);
    }

    showError(message) {
      this.flash(this.querySelector('[data-hp-sc-error]'), message);
    }

    showNotice(message) {
      var notice = this.querySelector('[data-hp-sc-notice]');
      if (!notice || !message) return;

      notice.textContent = '';
      notice.appendChild(el('span', '', message));

      var dismiss = el('button', '', '×');
      dismiss.type = 'button';
      dismiss.setAttribute('aria-label', 'Dismiss');
      notice.appendChild(dismiss);

      notice.hidden = false;
    }

    hideNotice() {
      var notice = this.querySelector('[data-hp-sc-notice]');
      if (!notice) return;
      notice.hidden = true;
      notice.textContent = '';
    }
  }

  function parsePotData(sections) {
    var html = sections && sections[POTS_ID];
    if (!html) return [];

    var script = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('#hp-side-cart-pots-data');
    if (!script) return [];

    try {
      var data = JSON.parse(script.textContent);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return [];
    }
  }

  customElements.define('hp-side-cart', HpSideCart);
})();
